import { getCompactRangeSelectorOptions, getRangeSelector } from './rangeSelectorConfig';
import { yearGridlineXAxisOptions } from './featureFlags';

/**
 * Highcharts option builders for the ratio/yield chart — a second,
 * independent stockChart rendered below the main chart, with one line (plus
 * an avg/±1σ overlay) per currently ticked metric. Its time window is fully
 * independent of the main chart's (see hooks/useAxisSync.js): each chart is
 * zoomed/panned separately and its overlays are a statistic of its own
 * visible window. Simpler than chartConfig.js's main-chart options
 * otherwise: single axis, no click-to-measure line.
 */

// See chartConfig.js's isShortLandscapeViewport docstring — same rationale,
// duplicated here rather than shared since it's a 1-line check and this
// file otherwise has no dependency on chartConfig.js.
const isShortLandscapeViewport = () => (
  typeof window !== 'undefined' && window.innerHeight <= 500
);

// Same mobile/tablet width band as the `maxWidth: 768` responsive rule
// below. Historically the compact rangeSelector styling (hide the date
// inputs, shrink button/label font) was itself applied *through* that
// responsive rule — but Highcharts' `RangeSelector#update()` unconditionally
// does `this.destroy(); this.init(chart); this.render();` on ANY
// `chart.update()` call that includes a `rangeSelector` key, INCLUDING one
// driven by our own `responsive.rules` (Highcharts' Responsive module calls
// `chart.update(mergedOptions, redraw, true)` on every rule re-evaluation),
// and that re-`init()` unconditionally re-clicks whichever button is
// `options.selected` using the axis's *current* `max` as of that exact
// moment. Confirmed via real-browser Playwright + direct console
// instrumentation: with the override applied this way, this chart's initial
// "last 15 years" default window intermittently collapsed to a few-day
// window anchored at the very first data point on narrow mobile widths
// (portrait AND landscape). (Historically this chart's axis was kept in
// lockstep with the main chart's via a bidirectional 'afterSetExtremes'
// listener in hooks/useAxisSync.js, so that bad window propagated to the
// main chart too, masquerading as a computeDefaultRange bug in
// rangeSelectorConfig.js when the actual defaults were verified correct
// throughout. The two charts' windows are now independent, so a bad window
// here would no longer corrupt the main chart — but the compact-styling
// fix below still stands on its own merits.)
// Baking the mobile-only styling into the *base* rangeSelector options
// instead — computed once, at options-construction time, like
// isShortLandscapeViewport already is — means `chart.update()` never
// touches `rangeSelector` again after the initial mount, which removes the
// whole class of risk. Trade-off: `getDefaultRatioChartOptions()` is itself
// only ever called once, as this hook's `useState` initializer
// (hooks/useStockData.js) — every later rebuild (a fetch, a ratio/yield
// toggle) reuses that same rangeSelector object rather than recomputing it
// — so this reflects only the viewport width at initial page load, not any
// later resize/orientation change. That's a minor, purely cosmetic gap
// (stale button/label sizing until the next full page load) next to
// corrupting the actual default date range.
const isNarrowMobileViewport = () => (
  typeof window !== 'undefined' && window.innerWidth <= 768
);

const getRatioRangeSelector = () => {
  const base = getRangeSelector();
  if (!isNarrowMobileViewport()) return base;
  return {
    ...base,
    ...getCompactRangeSelectorOptions(),
  };
};

export const getRatioYAxisTitle = (ratioMode) => (ratioMode === 'yield' ? 'Yield (%)' : 'Ratio');

export const getRatioValueTooltip = (ratioMode) => (
  ratioMode === 'yield'
    ? { valueSuffix: '%', valueDecimals: 2 }
    : { valueDecimals: 2 }
);

export const getDefaultRatioChartOptions = (ratioMode = 'ratio') => ({
  yAxis: [{
    title: { text: getRatioYAxisTitle(ratioMode) },
    // Ratio mode emits negative, sign-crossing, and above-ceiling
    // multiples as gaps (see buildRatioSeries and ADR 0001's second
    // update), so zero is its natural lower bound. Yield remains finite
    // through zero and may be negative during a loss-making period; leave
    // its lower bound automatic so that information remains visible.
    floor: ratioMode === 'ratio' ? 0 : null,
  }],
  xAxis: [{
    crosshair: false,
    ordinal: false,
    // Vertical per-year gridlines, gated behind the VERTICAL_YEAR_GRIDLINES
    // feature flag (currently off — shared helper from config/featureFlags.js).
    ...yearGridlineXAxisOptions(),
  }],
  chart: {
    animation: true,
    height: '35%',
    zIndex: 0,
    zoomType: 'x',
  },
  // See chartConfig.js's matching comment -- no explicit index; the default
  // now correctly resolves to "15y". Mobile-width styling is baked in here
  // directly (see getRatioRangeSelector's docstring) rather than through the
  // responsive rule below.
  rangeSelector: getRatioRangeSelector(),
  responsive: {
    rules: [
      {
        // Same mobile compaction as the main chart (see chartConfig.js),
        // plus this chart's own navigator is switched off: on a phone the
        // second navigator is pure vertical clutter. (On wider screens it
        // stays on and now acts as this chart's own independent window
        // control — the two charts' windows are no longer synced; see
        // hooks/useAxisSync.js.)
        condition: {
          maxWidth: 768,
        },
        chartOptions: {
          chart: {
            spacingLeft: 4,
            spacingRight: 4,
          },
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            itemDistance: 8,
            itemMarginBottom: 4,
            padding: 4,
            itemStyle: { fontSize: '11px' },
          },
          // rangeSelector styling is intentionally NOT overridden here — see
          // getRatioRangeSelector's docstring above (this rule's own
          // chartOptions object still runs through Highcharts'
          // `chart.update()`, so anything placed here is safe *except* a
          // `rangeSelector` key specifically).
          xAxis: [{
            labels: { style: { fontSize: '10px' } },
          }],
          yAxis: [{
            labels: { style: { fontSize: '10px' } },
          }],
          navigator: {
            enabled: false,
          },
          // Disabling the navigator above doesn't also disable Highcharts'
          // separate scrollbar control — left on, it renders as an orphaned
          // drag bar with no navigator context above it (confirmed via
          // real-browser screenshot), which is exactly the "second
          // independent control" clutter this rule is meant to remove, not
          // a smaller version of it.
          scrollbar: {
            enabled: false,
          },
        },
      },
      {
        // Phones in portrait: `chart.height: '35%'` (below) is a percentage
        // of the chart's own *width* — once that width narrows to a phone
        // portrait size, 35% of it is too little absolute height for the
        // range-selector row + legend to leave any room for the actual P/E
        // line, collapsing the plot to 0px (confirmed via real-browser
        // screenshot). Scoped to maxWidth 480 (not the full 768 mobile
        // band above) because wider phone/tablet-portrait widths already
        // get enough absolute height out of the same 35% and don't need
        // flooring — this would only needlessly shrink an already-fine
        // chart there.
        // 180 (the original floor here) still only left ~27px of actual
        // plot area once the range-selector row + legend were accounted
        // for — visually indistinguishable from the 0px collapse this rule
        // was meant to fix (confirmed via real-browser Playwright
        // screenshot: the P/E line rendered as a single flat sliver with no
        // visible variation). 240 leaves a legible plot area at the same
        // widths this rule already targets.
        condition: {
          maxWidth: 480,
        },
        chartOptions: {
          // Maintainer's real-device retest (2026-08-23) still wanted this
          // noticeably bigger; 320 (+33% over 240) leaves a legible plot
          // area at the same widths this rule already targets (confirmed
          // via real-browser Playwright screenshot).
          chart: { height: 320 },
        },
      },
      {
        // Landscape phones / short viewports — see chartConfig.js's
        // matching rule. 140 (the original floor here) left literally 0px
        // of plot area once the range-selector row + legend were
        // accounted for (confirmed via real-browser Playwright screenshot
        // and direct plot-background measurement) — a full collapse, not
        // just a thin sliver. 190 leaves a legible, if still compact, plot
        // area within a landscape phone's limited vertical budget.
        condition: {
          minWidth: 601,
          maxWidth: 1024,
          callback: function () { return isShortLandscapeViewport(); },
        },
        chartOptions: {
          // Maintainer's real-device retest (2026-08-23) still wanted this
          // noticeably bigger; 250 (+32% over 190) stays comfortably under
          // a landscape phone's ~375-390px innerHeight (confirmed via
          // real-browser Playwright screenshot at 812x375 / 844x390).
          chart: { height: 250 },
        },
      },
    ],
  },
  // Highcharts' stockChart constructor forces legend.enabled: false
  // internally regardless of the core default — must be re-enabled
  // explicitly now that multiple ticked metrics need a legend to
  // distinguish by color.
  legend: {
    enabled: true,
  },
  tooltip: {
    shared: true,
  },
  plotOptions: {
    series: {
      states: {
        hover: { lineWidthPlus: 0 },
        inactive: { enabled: false },
      },
    },
  },
  series: [],
});
